import { features } from '../config/steps'
import { useParallax } from '../hooks/useParallax'
import { useReveal } from '../hooks/useReveal'
import { Icon } from './ui/Icon'

/** Each card drifts by a slightly different amount, so the row gains depth. */
const DRIFT = [26, 12, 22]

/** Cards deal in one at a time, left to right — wide enough apart to actually read as sequential. */
const CARD_LEAD_IN = 150
const CARD_STAGGER = 260

export function WhyChoose() {
  // Reveals once and stays (see HowItWorks.jsx for why `once: false` was
  // removed — it made content re-hide and cause visible blank/white gaps
  // during ordinary scrolling).
  const ref = useReveal()
  useParallax(ref)

  return (
    <section
      id="why-roammyway"
      ref={ref}
      className="scroll-mt-20 bg-linear-to-b from-brand-50 to-white pt-12 pb-12 sm:pt-16 sm:pb-16 dark:from-[var(--surface-soft)] dark:to-[var(--surface)]"
    >
      <div className="mx-auto max-w-[88rem] px-5 sm:px-8">
        <header className="mx-auto max-w-3xl text-center" data-parallax="44">
          <p className="text-base font-bold text-brand-600 dark:text-brand-300" data-reveal>
            Why Choose RoamMyWay?
          </p>
          <h2
            className="mt-4 text-[clamp(2rem,4.4vw,3.4rem)] leading-[1.08] font-bold"
            data-reveal="rise"
            style={{ '--reveal-delay': '90ms' }}
          >
            Planning That Respects Your Time And Budget
          </h2>
          <span
            className="mx-auto mt-7 block h-px w-24 bg-brand-500/60"
            data-reveal="draw"
            style={{ '--reveal-delay': '260ms' }}
            aria-hidden="true"
          />
        </header>

        <ul className="mt-14 grid gap-6 lg:mt-18 lg:grid-cols-3 lg:gap-8">
          {features.map((feature, i) => (
            <li
              key={feature.title}
              data-reveal="rise"
              data-parallax={DRIFT[i]}
              style={{ '--reveal-delay': `${CARD_LEAD_IN + i * CARD_STAGGER}ms` }}
            >
              {/* Card styling lives here, not on the <li> — the li owns the
                  reveal transform and the parallax translate. */}
              <article className="group flex h-full flex-col rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface)] p-8 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-brand-200 hover:shadow-[0_28px_54px_-30px_rgb(45_101_113/0.45)] sm:p-10 dark:bg-[var(--surface-soft)] dark:hover:border-brand-800">
                {/* The icon lands just after its card has settled. */}
                <span
                  className="grid h-13 w-13 place-items-center rounded-xl bg-brand-600 text-white shadow-[0_10px_24px_-12px_rgb(45_101_113/0.9)]"
                  data-reveal="pop"
                  style={{ '--reveal-delay': `${CARD_LEAD_IN + i * CARD_STAGGER + 260}ms` }}
                >
                  <Icon name={feature.icon} className="h-6 w-6" strokeWidth={1.7} />
                </span>
                <h3 className="mt-7 text-xl leading-snug font-semibold text-accent-500 sm:text-[1.35rem]">{feature.title}</h3>
                <p className="mt-4 text-[1.02rem] leading-relaxed">{feature.description}</p>
              </article>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
